import type { TCoreImageStyleProps, TCoreLayoutStyleProps } from '@core/widgets';
import { memoByPalette, SemanticColor, TSemanticColors } from '../../../theme/colors';
import { TVisualOption } from '../../../types/options';

/**
 * Palette key of the visual element.
 */
export type TVisualHierarchy = NonNullable<TVisualOption['iconHierarchy']>;

export type TVisualVariant = NonNullable<TVisualOption['iconVariant']>;

/**
 * Container palette of the visual element: the base colors plus every per-state variant
 * (`disabled`, `hovered`, `pressed`) in a single object. It is spread into CoreLayoutView's
 * `styleProps` as a whole, so the core widget — not the caller — resolves the active state.
 */
export type TVisualContainerPalette = Pick<TCoreLayoutStyleProps, 'backgroundColor' | 'borderColor' | 'disabled' | 'hovered' | 'pressed'>;

/** Icon palette of the visual element: both states in one object, resolved by CoreImageView. */
export type TVisualIconPalette = Pick<TCoreImageStyleProps, 'disabled' | 'iconColor'>;
/**
 * Container palette of the `circle` variant per hierarchy.
 * `hovered` / `pressed` are not part of the map — they are supplied per call site via the `hovered` prop.
 */
const buildVisualCircleMap = (colors: TSemanticColors): Record<TVisualHierarchy, TVisualContainerPalette> => ({
    primary: {
        backgroundColor: colors[SemanticColor.BgEmphasisedMuted],
        disabled: {
            backgroundColor: 'transparent',
            borderColor: colors[SemanticColor.BorderEmphasisedDisabled],
        },
    },
    highlighted: {
        backgroundColor: 'var(--purple_8_tr)',
        disabled: {
            backgroundColor: 'transparent',
            borderColor: 'var(--purple_16_tr)',
        },
    },
    emphasised: {
        backgroundColor: colors[SemanticColor.BgEmphasised],
        // v2: solid grey, as in the design (the 40 % white on-dark-fill icon disappears on the muted tint);
        // v1 and lulox keep the muted tint (see `bg/emphasised/disabled` in the theme css).
        disabled: {
            backgroundColor: colors[SemanticColor.BgEmphasisedDisabled],
        },
    },
    secondary: {
        backgroundColor: colors[SemanticColor.BgSecondary],
        disabled: {
            backgroundColor: 'transparent',
            borderColor: colors[SemanticColor.BorderPrimaryDisabled],
        },
    },
    secondaryAlt: {
        backgroundColor: 'var(--white)',
        disabled: {
            backgroundColor: 'transparent',
            borderColor: 'var(--purple_16_tr)',
        },
    },
    destructive: {
        backgroundColor: colors[SemanticColor.BgErrorMuted],
        disabled: {
            backgroundColor: 'transparent',
            borderColor: colors[SemanticColor.BorderErrorDisabled],
        },
    },
});
export const getVisualCircleMap = memoByPalette(buildVisualCircleMap);

/**
 * Icon palette per variant, shaped as CoreImageView style props.
 * Both states are handed to CoreImageView (`iconColor` / `iconColorDisabled`) so it picks
 * the one matching its own `isDisabled` state.
 */
const buildVisualIconMap = (colors: TSemanticColors): Record<TVisualVariant, Record<TVisualHierarchy, TVisualIconPalette>> => ({
    simple: {
        primary: { iconColor: colors[SemanticColor.IconPrimary], disabled: { iconColor: colors[SemanticColor.IconPrimaryDisabled] } },
        highlighted: { iconColor: 'var(--white_base)', disabled: { iconColor: 'var(--white_base)' } },
        emphasised: { iconColor: colors[SemanticColor.IconEmphasised], disabled: { iconColor: colors[SemanticColor.IconEmphasisedDisabled] } },
        secondary: { iconColor: colors[SemanticColor.IconSecondary], disabled: { iconColor: colors[SemanticColor.IconPrimaryDisabled] } },
        secondaryAlt: { iconColor: 'var(--black)', disabled: { iconColor: 'var(--grey_40_lc)' } },
        destructive: { iconColor: colors[SemanticColor.IconError], disabled: { iconColor: colors[SemanticColor.IconErrorDisabled] } },
    },
    circle: {
        primary: { iconColor: colors[SemanticColor.IconEmphasised], disabled: { iconColor: colors[SemanticColor.IconEmphasisedDisabled] } },
        highlighted: { iconColor: 'var(--white_base)', disabled: { iconColor: 'var(--white_base)' } },
        // Inverted, not on-dark-fill: the `bg/emphasised` circle turns white in the dark theme.
        // Disabled intentionally stays on on-dark-fill (as in the design): the disabled circle sits on the
        // muted fill, and inverted/disabled is solid white in the light theme instead of 40 % white.
        emphasised: {
            iconColor: colors[SemanticColor.IconPrimaryInverted],
            disabled: { iconColor: colors[SemanticColor.IconPrimaryOnDarkFillDisabled] },
        },
        secondary: { iconColor: colors[SemanticColor.IconPrimary], disabled: { iconColor: colors[SemanticColor.IconPrimaryDisabled] } },
        secondaryAlt: { iconColor: 'var(--black)', disabled: { iconColor: 'var(--grey_40_lc)' } },
        destructive: { iconColor: colors[SemanticColor.IconError], disabled: { iconColor: colors[SemanticColor.IconErrorDisabled] } },
    },
});
export const getVisualIconMap = memoByPalette(buildVisualIconMap);

/** Ring thickness (px) drawn around the circle on hover / press. */
export const HOVER_INSET = 6;
